import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface ConfirmLoadRatingDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  previousWindLoad: number;
  newWindLoad: number;
  previousSnowLoad: number;
  newSnowLoad: number;
  isSubmitting?: boolean;
}

const ConfirmLoadRatingDialog: React.FC<ConfirmLoadRatingDialogProps> = ({
  open,
  onClose,
  onConfirm,
  previousWindLoad,
  newWindLoad,
  previousSnowLoad,
  newSnowLoad,
  isSubmitting = false,
}) => {
  const [confirmed, setConfirmed] = useState(false);

  // Defensive guard: the ASCE API can occasionally yield non-numeric values for
  // unsupported locations. Never let "NaN" reach the user — fall back to a dash.
  const formatLoad = (value: number, unit: string): string =>
    Number.isFinite(value) ? `${value} ${unit}` : `— ${unit}`;
  const hasInvalidNewValues = !Number.isFinite(newWindLoad) || !Number.isFinite(newSnowLoad);

  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  const handleConfirm = () => {
    setConfirmed(false);
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Load Rating Update</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          The Job Location has changed. The ASCE API has returned updated load values for the new
          coordinates:
        </Typography>
        <Box sx={{ bgcolor: '#f0f7ff', borderRadius: 1, p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Wind Load:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatLoad(previousWindLoad, 'mph')} &rarr; {formatLoad(newWindLoad, 'mph')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Snow Load:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatLoad(previousSnowLoad, 'psf')} &rarr; {formatLoad(newSnowLoad, 'psf')}
            </Typography>
          </Box>
        </Box>
        {hasInvalidNewValues ? (
          <Typography variant="body2" color="error">
            Updated load values could not be determined for this location. Please verify the address
            and coordinates, then try again.
          </Typography>
        ) : (
          <FormControlLabel
            control={
              <Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            }
            label="I confirm the updated Load Rating values"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!confirmed || isSubmitting || hasInvalidNewValues}
        >
          {isSubmitting ? 'Updating...' : 'Accept & Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmLoadRatingDialog;
